import { reportClientFault } from "./client-report";
import { engineTag } from "./crash-context";

/**
 * DID THE APP ACTUALLY PAINT? — the check nothing was doing.
 *
 * Every other beacon fires on a THROWN error or a connection GIVING UP. A page that renders half
 * its UI does neither: it throws nothing, gives up on nothing, and reports nothing. On 2026-08-01
 * a player's table came up with cards and avatars but no felt, header, score or controls, on a
 * black background with one seat adrift — and the entire server side of that event was silence.
 *
 * That shape is a STYLING failure, and it is cheap to detect directly. Two independent signals,
 * because they fail differently:
 *   • NO STYLESHEET AT ALL (`document.styleSheets` empty) — the `<link>` never loaded, or loaded
 *     and was rejected. Images still render, laid-out DOM does not, text goes invisible against a
 *     dark WebView, and absolutely-positioned seats collapse into the corner.
 *   • STYLESHEET PRESENT BUT TOKENS UNRESOLVED — every colour in this app is
 *     `rgb(var(--c-…) / <alpha>)`, so if the token block is missing the layout survives while
 *     every background and text colour resolves to nothing. A different failure with a similar
 *     look, and only this second probe can tell them apart.
 *
 * ⚠️ TWO THINGS THE FIRST VERSION LACKED, ADDED AFTER ITS FIRST REAL REPORT TAUGHT US NOTHING.
 * It fired `shape=tokens-unresolved sheets=2 token=(empty)` on 2026-08-01 and the investigation
 * dead-ended: a healthy client measurably has ONE sheet, so two was abnormal — but the beacon
 * recorded how MANY sheets, never WHICH, so the one question that would have ended it («what were
 * those two?») had no recorded answer. And the user kept playing for half an hour afterwards,
 * which suggests the page was fine and the probe caught a transient moment. Hence:
 *   1. {@link sheetNames} — the identities go in the report, so the next one names the culprit.
 *   2. {@link RECHECK_MS} — a broken reading is CONFIRMED before it is reported. A race heals and
 *      is never reported; a real breakage persists and is. That is what makes this trustworthy
 *      enough to carry ERROR severity instead of being recorded and ignored.
 *
 * Deliberately NOT a repair: it says what happened, it does not guess a fix.
 */

/** Checked because EVERY colour in the app derives from it — if this is empty, nothing is painted right. */
const SENTINEL_TOKEN = "--c-surface";

/**
 * How long to wait before confirming a broken reading.
 *
 * Long enough that anything transient — a sheet still being swapped, a document mid-restore — has
 * resolved, short enough that a genuinely broken page is still reported while the user is looking
 * at it. A first read is a suspicion; only the second is evidence.
 */
export const RECHECK_MS = 2000;

/**
 * How long to keep watching a CONFIRMED-broken page before calling the failure permanent.
 *
 * ⚠️ THIS IS THE FIELD THE PROBE WAS MISSING, AND WITHOUT IT THE RE-CHECK COULD NOT DO ITS JOB.
 * The re-check was added to separate a race from a real breakage — but 2 s cannot separate them
 * when the phenomenon has a multi-second tail: a stylesheet that lands at +4 s reads EXACTLY like
 * one that never lands. Every report we had said «not applied at +3.2 s» and nothing whatsoever
 * about what happened next, so a several-second flash of unstyled content and a permanently
 * unstyled page arrived as the same ERROR (measured 2026-08-04: two iOS clients reported this,
 * and both went on playing within 30 s — they were the FIRST kind, paging as if they were the
 * second).
 *
 * So the report now waits for the OUTCOME and names it. 15 s is long enough to catch the tail we
 * have actually observed and short enough that the player is usually still there.
 */
export const OUTCOME_DEADLINE_MS = 15_000;
/** How often to look while waiting for the outcome. */
export const OUTCOME_POLL_MS = 500;

/** At most this many sheet identities in a report — `detail` is clamped server-side to 900 chars. */
const MAX_SHEET_NAMES = 6;

/**
 * RE-FETCH A STYLESHEET THAT NEVER CAME GOOD — the one thing this file does beyond reporting.
 *
 * Grounded in what was measured on 2026-08-04, not in hope: Chromium does NOT store a severed
 * response (every reload re-requested, and the first healthy one recovered fully), and the same
 * device on the same network refetched the sheet complete three times minutes later. The bytes are
 * usually there for the asking — nothing was retrying for them.
 *
 * Two deliberate choices:
 *   • APPEND, never replace on suspicion. CSS is additive and idempotent, so if the original link
 *     eventually lands as well the computed styles are identical. Removing it before anything else
 *     has arrived could throw away a sheet that was about to land. Only once a copy has DELIVERED
 *     are the copies still in flight removed — {@link evictStuckCopies}, which WebKit needs.
 *   • VARY THE URL. `/assets/` is served `immutable`, so re-adding the same href risks being
 *     answered from the very cache entry that is already no good. Same trick, same reason as the
 *     card-faces chunk loader's `?retry=N`.
 */
const RETRY_PARAM = "retry";

/**
 * WHEN each re-fetch fires, measured in `afterMs` — the same clock the beacon reports, so a
 * beacon's `refetch=N` can be read straight off this list.
 *
 * The first two are back-to-back because of what 2026-08-04 established: a severed response is not
 * cached, and the bytes are usually there for the asking — so ask again, immediately, twice.
 *
 * ⚠️ THE THIRD IS A DIFFERENT BET, AND ITS SPACING IS THE ENTIRE REASON IT EXISTS. On 2026-08-08 the
 * origin log killed the truncation theory for the failures that remain: two reporting clients were
 * SERVED the stylesheet complete — six times between them, every one a logged `200 17650` — and were
 * still unstyled at +15 s, while their large chunks came back severed at exact TLS record boundaries
 * in the same minutes. That is the whole EXCHANGE being lost on the path, not a body being cut, and
 * it outlasts a 3 s cadence: +2 s and +5 s are two shots inside ONE outage, and a naive third at
 * +8 s would be a third shot inside it. +10 s is the first attempt that is plausibly a LATER ROLL OF
 * THE NETWORK — the only thing ever observed to actually work here (the same device fetched the
 * sheet complete minutes later).
 *
 * ⚠️ THE LAST ENTRY MUST LEAVE ROOM TO **OBSERVE** ITS RESULT, or the beacon reports `outcome=never`
 * about a sheet that was still in flight and the ledger records a failure the retry never had a
 * chance at. Measured recoveries land ~1 s after their attempt (2026-08-06: +3000 after a +2000
 * attempt, +6000 after a +5000 one), so the 5 s tail against {@link OUTCOME_DEADLINE_MS} is ~5×
 * margin. **Move the last attempt later and you must move the deadline with it** — pinned by a test.
 *
 * ⚠️ CORRECTED 2026-10-21 (card-77): on WebKit «served complete, still unstyled» is ALSO exactly
 * what the engine does with a retry that arrived while the original was still pending — it is
 * loaded and not applied. See {@link evictStuckCopies}. The schedule itself is unchanged.
 */
export const REFETCH_SCHEDULE_MS = [RECHECK_MS, 5_000, 10_000] as const;

/** One report per distinct shape per session: a broken paint stays broken, and a beacon per
 *  visibility flip would bury the signal it exists to raise. */
const reported = new Set<string>();

export interface StyleHealth {
  sheets: number;
  /** What those sheets ARE — the field whose absence dead-ended the first investigation. */
  names: string;
  token: string;
  ok: boolean;
  shape: "ok" | "no-stylesheet" | "tokens-unresolved";
  /** Rules per sheet — `blocked` where `cssRules` throws. See {@link ruleCounts}. */
  rules: string;
  /** How many `<link rel=stylesheet>` ELEMENTS exist, whatever `styleSheets` says. */
  links: number;
  /** `webkit` | `blink` | `gecko` — recorded, not inferred from the sheet names. */
  engine: string;
  /** How the stylesheet's bytes arrived, or did not. See {@link cssTransfer}. */
  css: string;
}

/**
 * ⚠️ `names` SAYS THE SHEET IS THERE. IT DOES NOT SAY THE SHEET LOADED.
 *
 * Measured in Chromium on 2026-08-04 against the real deployed `index-DbHGhEMo.css`, with a green
 * control: a `<link>` whose load FAILS is still listed in `document.styleSheets` **with its href**
 * and contributes **zero rules**. So `names=index-….css` and `names=<inline style>,<inline style>`
 * are the SAME fault — the app's stylesheet is not applied — seen by two engines (Chromium keeps
 * the failed sheet listed; WebKit omits a sheet that has not loaded). Confirmed on the ledger:
 * of 37 reports, the named-sheet variant was Android 9/9 and the inline variant iOS 25/25.
 *
 *   what happened            transferSize  encodedBodySize  decodedBodySize  cssRules
 *   healthy network fetch          18008            17708            95060      1028
 *   healthy CACHE HIT                  0            17708            95060      1028
 *   request never answered             0                0                0     throws
 *   truncated mid-transfer          8300             8000            43513         0
 *
 * All three failures produced a byte-identical beacon before these fields existed, which is why
 * the 2026-08-04 10:12 report could not be attributed to a cause. `rules` + `css` separate them.
 */

/** Rules per sheet: `1028,1` healthy · `0,1` a body that was discarded · `blocked,1` no response. */
function ruleCounts(doc: Document): string {
  try {
    const counts = Array.from(doc.styleSheets)
      .slice(0, MAX_SHEET_NAMES)
      .map((s) => {
        try {
          // A cross-origin OR failed sheet throws SecurityError here — that throw IS the signal.
          return String((s as CSSStyleSheet).cssRules.length);
        } catch {
          return "blocked";
        }
      });
    return counts.join(",") || "(none)";
  } catch {
    return "(unreadable)";
  }
}

/**
 * The `<link rel=stylesheet>` ELEMENT count.
 *
 * On WebKit a sheet that has not loaded is absent from `styleSheets` altogether, so the report
 * reads as though the app never had a stylesheet at all. `links=1` says the element is in the
 * document and simply has not come good — the distinction the iOS reports could not make.
 * `-1` = the document would not answer (never guess a count).
 */
function linkCount(doc: Document): number {
  try {
    return doc.querySelectorAll('link[rel~="stylesheet"]').length;
  } catch {
    return -1;
  }
}

/**
 * How the stylesheet's bytes arrived — from `PerformanceResourceTiming`, which is already in the
 * page and costs nothing. The tag names WHAT WAS OBSERVED, never a guess at who caused it:
 *   `empty`  — an entry with nothing in it: the request was never answered.
 *   `wire`   — bytes crossed the network. With the sheet unapplied, they did not all arrive.
 *   `cached` — nothing crossed the wire yet a whole body decoded: it came from the HTTP cache.
 *   `none`   — no entry at all (the fetch never started, or the buffer was cleared).
 */
/**
 * The app's stylesheet URL — from `styleSheets` when it is listed, else from the `<link>` ELEMENT.
 *
 * The element fallback is the load-bearing half: on WebKit a sheet that has not loaded is absent
 * from `styleSheets` altogether (that is the whole `names=<inline>,<inline>` shape), so in exactly
 * the case we want to retry, the element is the only place the href exists. Each source gets its
 * OWN try, so a document that throws on `styleSheets` can still yield the element's href.
 */
function styleHref(doc: Document): string | null {
  try {
    const fromSheets = Array.from(doc.styleSheets)
      .map((s) => s?.href ?? null)
      .find((h): h is string => !!h);
    if (fromSheets) return fromSheets;
  } catch {
    // fall through — the element below is the more reliable source anyway
  }
  try {
    return doc.querySelector<HTMLLinkElement>('link[rel~="stylesheet"][href]')?.href ?? null;
  } catch {
    return null;
  }
}

/**
 * Append one cache-busting `<link>` for the app's stylesheet. Returns whether it went in — a
 * document that will not answer yields `refetch=0` in the report rather than a silent no-op.
 * `arm` is handed the link BEFORE it goes in, so its `load` cannot be missed.
 */
function refetchStylesheet(
  doc: Document,
  base: string,
  attempt: number,
  arm: (link: HTMLLinkElement) => void,
): boolean {
  try {
    const url = new URL(base, doc.baseURI || undefined);
    url.searchParams.set(RETRY_PARAM, String(attempt));
    const link = doc.createElement("link");
    link.rel = "stylesheet";
    link.href = url.toString();
    arm(link);
    doc.head.appendChild(link);
    return true;
  } catch {
    return false;
  }
}

/** The path a stylesheet URL points at — query and hash dropped, so every `?retry=N` is one sheet. */
function pathOf(href: string, doc: Document): string | null {
  try {
    return new URL(href, doc.baseURI || undefined).pathname;
  } catch {
    return null;
  }
}

/** Did this `<link>` deliver a sheet that carries rules? A pending one has no `sheet` yet. */
function delivered(link: HTMLLinkElement): boolean {
  try {
    const sheet = link.sheet as CSSStyleSheet | null;
    return !!sheet && sheet.cssRules.length > 0;
  } catch {
    return false; // blocked — it cannot be counted as having delivered
  }
}

/** Every `<link rel=stylesheet>` for the app's OWN sheet, original and retries — nothing on another path. */
function copiesOf(doc: Document, path: string): HTMLLinkElement[] {
  try {
    return Array.from(doc.querySelectorAll<HTMLLinkElement>('link[rel~="stylesheet"]')).filter(
      (l) => !!l && typeof l.href === "string" && pathOf(l.href, doc) === path,
    );
  } catch {
    return [];
  }
}

/**
 * ⚠️ WEBKIT DOES NOT APPLY A STYLESHEET THAT LOADS WHILE A SIBLING ONE IS STILL PENDING — so a
 * retry that ARRIVES does nothing for as long as the stuck original sits in the document.
 *
 * Read in WebKit's source (card-77, 2026-10-21; unchanged since at least 2021, so every iOS in the
 * reports, 15.5 → 18.7): every active `<link rel=stylesheet>`, script-inserted ones included, is a
 * PENDING sheet until it finishes (`HTMLLinkElement::process()` → `addPendingSheet(Active)`), and a
 * sheet that finishes re-collects the active set only when NO sheet is pending any more
 * (`Style::Scope::didRemovePendingStylesheet()` returns early on `hasPendingSheets()`). Until then
 * it is parsed onto its element and neither applied nor listed in `document.styleSheets`. Removing
 * a `<link>` re-collects unconditionally (`removedFromAncestor()` → `removeStyleSheetCandidateNode()`
 * → `didChangeActiveStyleSheetCandidates()`). Blink applies each sheet as it loads, which is why
 * every `outcome=never` on the ledger is WebKit.
 *
 * The prod record, 10-07 → 10-18: `?retry=1/2/3` were answered 200 with the full 20,000 B on
 * schedule while the original's request never reached us — and the page stayed unstyled; where
 * the original DID reach us after `?retry=1` had been served (7 of 7 traces), the page styled only
 * when the original completed, 1–18 s later. The same rule cuts the other way: a retry that stalls
 * holds back an original that has arrived (10-11 20:05: original +4 s, styled +17 s).
 *
 * So: when any copy of the app's sheet DELIVERS (loaded, carrying rules), every other copy that has
 * not is removed — that cancels its load and makes WebKit re-collect, which applies the one that
 * arrived. A copy that delivered is never removed, a sheet on another path is never touched, and
 * nothing is removed on suspicion. Returns how many copies went (`dropped=` in the beacon).
 */
function evictStuckCopies(doc: Document, winner: HTMLLinkElement, path: string): number {
  if (!delivered(winner)) return 0;
  let dropped = 0;
  for (const link of copiesOf(doc, path)) {
    if (link === winner || delivered(link)) continue;
    try {
      link.remove();
      dropped += 1;
    } catch {
      // a document that will not let go keeps it — the probe never becomes the fault
    }
  }
  return dropped;
}

/**
 * WHICH sheet actually came good — `retry` or `original`.
 *
 * ⚠️ WITHOUT THIS THE FIX CANNOT BE JUDGED. Of the first 11 outcome-bearing beacons, FIVE already
 * recovered unaided, so a rise in `outcome=recovered` after shipping a retry would prove nothing at
 * all. Only a retry-tagged sheet that is present AND carrying rules earns `retry`; anything else is
 * `original`. The bias is deliberately toward under-claiming.
 */
function resolvedVia(doc: Document, retried: boolean): string {
  if (!retried) return "n/a";
  try {
    for (const sheet of Array.from(doc.styleSheets)) {
      const href = sheet?.href ?? "";
      if (!href.includes(`${RETRY_PARAM}=`)) continue;
      try {
        if ((sheet as CSSStyleSheet).cssRules.length > 0) return "retry";
      } catch {
        // blocked — it cannot be claimed as the rescuer
      }
    }
    return "original";
  } catch {
    return "?";
  }
}

function cssTransfer(doc: Document): string {
  try {
    const href = styleHref(doc);
    if (!href) return "none";
    const entries = performance.getEntriesByType("resource") as PerformanceResourceTiming[];
    const e = entries.find((r) => r.name === href);
    if (!e) return "none";
    const x = e.transferSize ?? 0;
    const en = e.encodedBodySize ?? 0;
    const de = e.decodedBodySize ?? 0;
    const tag = x === 0 && de > 0 ? "cached" : x === 0 && en === 0 && de === 0 ? "empty" : "wire";
    return `${tag}(x:${x},e:${en},d:${de})`;
  } catch {
    return "?";
  }
}

/**
 * Identify each stylesheet: the file's basename, or the tag for an injected one.
 *
 * `href` is readable even on a cross-origin sheet (only `cssRules` is blocked), so this works
 * regardless of where a sheet came from — and a sheet from somewhere unexpected is precisely what
 * we are trying to catch.
 */
function sheetNames(doc: Document): string {
  try {
    const all = Array.from(doc.styleSheets);
    const names = all.slice(0, MAX_SHEET_NAMES).map((s) => {
      const href = s.href;
      if (href) return href.split("/").pop() ?? href;
      const tag = s.ownerNode instanceof Element ? s.ownerNode.tagName.toLowerCase() : "?";
      return `<inline ${tag}>`;
    });
    if (all.length > MAX_SHEET_NAMES) names.push(`+${all.length - MAX_SHEET_NAMES} more`);
    return names.join(",") || "(none)";
  } catch {
    return "(unreadable)";
  }
}

/** Pure so it is testable without a browser lifecycle; reads the live document. */
export function inspectStyles(doc: Document = document): StyleHealth {
  let sheets = 0;
  let token = "";
  try {
    sheets = doc.styleSheets.length;
    token = getComputedStyle(doc.documentElement).getPropertyValue(SENTINEL_TOKEN).trim();
  } catch {
    // A throwing probe must never be the fault it reports — treat as unknown-but-ok.
    return {
      sheets: -1,
      names: "(unreadable)",
      token: "",
      ok: true,
      shape: "ok",
      rules: "(unreadable)",
      links: -1,
      engine: "?",
      css: "?",
    };
  }
  const names = sheetNames(doc);
  // The forensics are gathered ONLY for a fault: a healthy page pays nothing, and reading
  // `cssRules` on every sheet of every resume is not free.
  const bad = (shape: "no-stylesheet" | "tokens-unresolved"): StyleHealth => ({
    sheets,
    names,
    token,
    ok: false,
    shape,
    rules: ruleCounts(doc),
    links: linkCount(doc),
    engine: engineTag(),
    css: cssTransfer(doc),
  });
  if (sheets === 0) return bad("no-stylesheet");
  if (token === "") return bad("tokens-unresolved");
  return {
    sheets,
    names,
    token,
    ok: true,
    shape: "ok",
    rules: "",
    links: -1,
    engine: "",
    css: "",
  };
}

/** Did the player leave (minimise / navigate away) while we were still watching? */
function pageIsGone(doc: Document): boolean {
  try {
    return doc.visibilityState === "hidden";
  } catch {
    return false;
  }
}

/**
 * Read, CONFIRM after {@link RECHECK_MS}, then WATCH until we know what actually happened.
 *
 * Resolves to the reading that was acted on: the first one when healthy, the confirming one when
 * not. A reading that heals inside the re-check window is deliberately NOT reported — a race that
 * fixes itself is not something anyone can act on, and reporting it is what would make this noisy
 * enough to need silencing again.
 *
 * ⚠️ THE REPORT POPULATION IS UNCHANGED — only its CONTENT is richer. Exactly the readings that
 * were reported before (still broken at the re-check) are reported now; the beacon simply waits to
 * say which of three things happened, so «a slow paint» and «an unstyled page» stop arriving as the
 * same fault. `outcome=recovered|left` is a WARN server-side, `outcome=never` an ERROR — which is
 * what finally makes the ERROR mean something a person should get up for.
 *
 * `afterMs` counts the delay we ASKED for, not wall-clock: a backgrounded WebView throttles timers,
 * and a made-up 40 s would be worse than an honest «we waited this long and it had not come good».
 * A page that goes away mid-watch reports `left` rather than dropping the signal we already earned.
 */
export async function checkStyles(
  when: string,
  doc: Document = document,
  wait: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
  gone: () => boolean = () => pageIsGone(doc),
): Promise<StyleHealth> {
  const first = inspectStyles(doc);
  if (first.ok) return first;

  await wait(RECHECK_MS);
  const confirmed = inspectStyles(doc);
  if (confirmed.ok) return confirmed; // it healed — a transient, not a fault

  // Claim the shape NOW, before the watch: two overlapping checks (mount + a resume) must not both
  // spend a watch and file the same fault twice.
  if (reported.has(confirmed.shape)) return confirmed;
  reported.add(confirmed.shape);

  // NOW TRY TO FIX IT. The re-check has already ruled out a race, so this is a page that is
  // genuinely unstyled in front of someone — and the watch below doubles as the instrument that
  // says whether the re-fetch worked (`outcome` + `via`), at no extra cost.
  const base = styleHref(doc);
  // Every copy of the app's sheet — the original, still pending, and each retry as it goes in — is
  // armed: whichever DELIVERS first evicts the copies still in flight (see `evictStuckCopies`). The
  // listener outlives the watch, so a copy that lands after the verdict still heals the page.
  const path = base === null ? null : pathOf(base, doc);
  let dropped = 0;
  const arm = (link: HTMLLinkElement): void => {
    if (path === null) return;
    try {
      link.addEventListener(
        "load",
        () => {
          dropped += evictStuckCopies(doc, link, path);
        },
        { once: true },
      );
    } catch {
      // an element that cannot listen is simply not armed
    }
  };
  if (path !== null) copiesOf(doc, path).forEach(arm);
  // `refetched` counts links that ACTUALLY went in (that is what `refetch=N` reports, and a 0 is the
  // documented "the whole thing is inert" bug signal); `nextAttempt` walks the schedule. They are
  // separate so a document that refuses `createElement` burns its attempt instead of being retried
  // on every 500 ms poll for the rest of the watch.
  let refetched = 0;
  let nextAttempt = 0;
  const tryRefetch = (): void => {
    if (base === null || nextAttempt >= REFETCH_SCHEDULE_MS.length) return;
    nextAttempt += 1;
    if (refetchStylesheet(doc, base, nextAttempt, arm)) refetched += 1;
  };
  tryRefetch(); // schedule[0] is RECHECK_MS — the instant the fault is CONFIRMED, never on suspicion

  let afterMs = RECHECK_MS;
  let outcome: "recovered" | "never" | "left" = "never";
  let last = confirmed;
  while (afterMs < OUTCOME_DEADLINE_MS) {
    if (gone()) {
      outcome = "left";
      break;
    }
    await wait(OUTCOME_POLL_MS);
    afterMs += OUTCOME_POLL_MS;
    const now = inspectStyles(doc);
    if (now.ok) {
      outcome = "recovered";
      last = now;
      break;
    }
    // LOOK FIRST, then re-fetch: a retry put in on the poll that sees the page come good has nothing
    // left to rescue, and on WebKit a stalled one is a pending head sheet for the rest of the session.
    if (nextAttempt < REFETCH_SCHEDULE_MS.length && afterMs >= REFETCH_SCHEDULE_MS[nextAttempt]!) {
      tryRefetch();
    }
  }

  reportClientFault("style-broken", {
    shape: confirmed.shape,
    when,
    sheets: confirmed.sheets,
    names: confirmed.names,
    // WHY the sheet is not applied — the three readings that tell the causes apart. Taken from
    // the CONFIRMING look, so they describe the same moment as `shape`/`names`.
    rules: confirmed.rules,
    links: confirmed.links,
    engine: confirmed.engine,
    css: confirmed.css,
    token: confirmed.token || "(empty)",
    // Kept: a second look agreed, which is what makes this not a first-read artifact.
    recheck: "still-broken",
    outcome,
    afterMs,
    // Did we try to repair it, and did that repair land? `via` is what stops a rise in
    // `outcome=recovered` being mistaken for this fix working (5 of the first 11 recovered
    // on their own). ~20 chars on a detail line that uses 232 of its 900-char clamp.
    refetch: refetched,
    via: resolvedVia(doc, refetched > 0),
    // How many undelivered copies were removed when another delivered (card-77) — on WebKit, that is
    // the gate met and opened. Counted up to the verdict; an eviction after it is not reported.
    dropped,
  });
  return last;
}

/**
 * Watch for the life of the app: once after the first paint, and again whenever it comes back to
 * the foreground — a Mini App is minimised and restored far more often than it is loaded, and a
 * WebView can drop a stylesheet under memory pressure while it is away, which is the case a
 * mount-only check would never see.
 */
export function watchStyles(): () => void {
  // After the first paint, not during it: a sheet is still being applied on the frame we mount.
  const first = window.setTimeout(() => void checkStyles("mount"), 1200);
  const onVisible = (): void => {
    if (document.visibilityState === "visible") void checkStyles("resume");
  };
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    window.clearTimeout(first);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

/** Test seam — the once-per-shape latch is module state. */
export function __resetStyleSentinelForTests(): void {
  reported.clear();
}
